import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { useReducer, useCallback, useEffect, useMemo } from 'react';
import { loadRecaptchaScript, waitForRecaptchaReady, isRecaptchaLoaded } from '../utils/recaptcha-loader.js';
import { RecaptchaContext } from './RecaptchaContext.js';

/**
 * Initial state for the reCAPTCHA context
 */ const initialState = {
    loadingState: 'idle',
    isReady: false,
    error: null,
    config: null
};
/**
 * Reducer for managing reCAPTCHA state
 */ function recaptchaReducer(state, action) {
    switch(action.type){
        case 'SET_LOADING_STATE':
            return swcHelpers.objectSpread({}, state, {
                loadingState: action.payload,
                isReady: action.payload === 'loaded',
                error: action.payload === 'error' ? state.error : null
            });
        case 'SET_ERROR':
            return swcHelpers.objectSpread({}, state, {
                error: action.payload,
                loadingState: action.payload ? 'error' : state.loadingState,
                isReady: false
            });
        case 'SET_CONFIG':
            return swcHelpers.objectSpread({}, state, {
                config: action.payload
            });
        case 'RESET':
            return initialState;
        default:
            return state;
    }
}
/**
 * Provider component for reCAPTCHA v3 integration
 */ function RecaptchaProvider({ config , children  }) {
    const [state, dispatch] = useReducer(recaptchaReducer, initialState);
    /**
   * Load the reCAPTCHA script
   */ const load = useCallback(swcHelpers.asyncToGenerator(function*() {
        if (!config || !config.siteKey) {
            dispatch({
                type: 'SET_ERROR',
                payload: 'reCAPTCHA site key is required'
            });
            return;
        }
        try {
            dispatch({
                type: 'SET_LOADING_STATE',
                payload: 'loading'
            });
            dispatch({
                type: 'SET_ERROR',
                payload: null
            });
            yield loadRecaptchaScript(config);
            yield waitForRecaptchaReady();
            dispatch({
                type: 'SET_LOADING_STATE',
                payload: 'loaded'
            });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to load reCAPTCHA';
            console.error('Failed to load reCAPTCHA script:', error);
            dispatch({
                type: 'SET_ERROR',
                payload: errorMessage
            });
            dispatch({
                type: 'SET_LOADING_STATE',
                payload: 'error'
            });
        }
    }), [
        config
    ]);
    /**
   * Execute reCAPTCHA for a specific action
   */ const execute = useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(options) {
            if (!state.isReady || !state.config) {
                throw new Error('reCAPTCHA is not ready. Make sure to load it first.');
            }
            if (!isRecaptchaLoaded()) {
                throw new Error('reCAPTCHA script is not loaded');
            }
            try {
                const token = yield window.grecaptcha.enterprise.execute(state.config.siteKey, {
                    action: options.action
                });
                return {
                    token,
                    action: options.action,
                    timestamp: Date.now()
                };
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Failed to execute reCAPTCHA';
                dispatch({
                    type: 'SET_ERROR',
                    payload: errorMessage
                });
                throw new Error(errorMessage);
            }
        });
        return function(options) {
            return _ref.apply(this, arguments);
        };
    }(), [
        state.isReady,
        state.config
    ]);
    /**
   * Reset the reCAPTCHA state
   */ const reset = useCallback(()=>{
        dispatch({
            type: 'RESET'
        });
    }, []);
    /**
   * Initialize the provider with config and auto-load if enabled
   */ useEffect(()=>{
        if (!config) {
            // No config provided, provider is inactive
            return;
        }
        dispatch({
            type: 'SET_CONFIG',
            payload: config
        });
        if (config.autoLoad !== false) {
            load();
        }
    }, [
        config,
        load
    ]);
    /**
   * Context value
   */ const contextValue = useMemo(()=>swcHelpers.objectSpread({}, state, {
            execute,
            load,
            reset
        })
    , [
        state,
        execute,
        load,
        reset
    ]);
    return /*#__PURE__*/ React.createElement(RecaptchaContext.Provider, {
        value: contextValue
    }, children);
}

export { RecaptchaProvider };
//# sourceMappingURL=RecaptchaProvider.js.map
